// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="Footer" set="mytaible-shadcn-studio" verified=tbd
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FooterLink {
  label: string;
  href: string;
}

export interface FooterSection {
  title: string;
  links: FooterLink[];
}

export interface FooterProps extends React.HTMLAttributes<HTMLElement> {
  layout?: 'marketing' | 'dashboard' | 'minimal';
  logo?: React.ReactNode;
  sections?: FooterSection[];
  bottomText?: string;
  /** Stick to the bottom of the nearest scroll container (position: sticky) */
  sticky?: boolean;
}

// ─── Footer ───────────────────────────────────────────────────────────────────

const Footer = React.forwardRef<HTMLElement, FooterProps>(
  ({ className, layout = 'marketing', logo, sections, bottomText, sticky, ...props }, ref) => {
    if (layout === 'minimal') {
      return (
        <footer
          ref={ref}
          className={cn(
            'border-t border-border bg-background py-6',
            sticky && 'sticky bottom-0 z-10',
            className,
          )}
          {...props}
        >
          <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
            {bottomText ?? `© ${new Date().getFullYear()} All rights reserved.`}
          </div>
        </footer>
      );
    }

    if (layout === 'dashboard') {
      return (
        <footer
          ref={ref}
          className={cn(
            'border-t border-border bg-background py-4',
            sticky && 'sticky bottom-0 z-10',
            className,
          )}
          {...props}
        >
          <div className="container mx-auto flex flex-col items-center justify-between gap-4 px-4 sm:flex-row">
            {logo && <div className="shrink-0">{logo}</div>}
            {sections && sections.length > 0 && (
              <nav className="flex flex-wrap items-center gap-4">
                {sections.flatMap((section) =>
                  section.links.map((link) => (
                    <a
                      key={`${section.title}-${link.label}`}
                      href={link.href}
                      className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {link.label}
                    </a>
                  )),
                )}
              </nav>
            )}
            <p className="text-sm text-muted-foreground">
              {bottomText ?? `© ${new Date().getFullYear()} All rights reserved.`}
            </p>
          </div>
        </footer>
      );
    }

    // marketing
    return (
      <footer
        ref={ref}
        className={cn(
          'border-t border-border bg-background',
          sticky && 'sticky bottom-0 z-10',
          className,
        )}
        {...props}
      >
        <div className="container mx-auto px-4 py-12">
          {(logo || (sections && sections.length > 0)) && (
            <div className="mb-8 grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-4">
              {logo && <div className="col-span-full lg:col-span-1">{logo}</div>}
              {sections?.map((section) => (
                <div key={section.title}>
                  <h3 className="mb-3 text-sm font-semibold text-foreground">{section.title}</h3>
                  <ul className="space-y-2">
                    {section.links.map((link) => (
                      <li key={link.href}>
                        <a
                          href={link.href}
                          className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                        >
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
          <div className="border-t border-border pt-8 text-center text-sm text-muted-foreground">
            {bottomText ?? `© ${new Date().getFullYear()} All rights reserved.`}
          </div>
        </div>
      </footer>
    );
  },
);
Footer.displayName = 'Footer';

export { Footer };
